import React, { ReactNode, startTransition, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Filter, OptionObject, Select, SeverityItem } from '@fabric-ui/components';
import { Box, SxProps } from '@mui/material';
import { getFieldTypeInfo } from '../../../utils/filterUtils';
import { IconVariant, ItemWithLogo } from '../../ItemWithLogo';
import { useExpressionBuilder } from '../ExpressionBuilderContext';
import { FilterOption } from '../types';
import { ExcludedOptionLabel } from './ExcludedOptionLabel';

const MAX_DISPLAYED_OPTIONS = 300;
// Select's renderOptionStyle prop takes sx, so this one stays MUI - it dims the whole option row, checkbox included.
const excludedOptionStyle: SxProps = { color: 'var(--color-content-base-disabled)' };
const emptyStyle: SxProps = {};

const SeverityOption = ({ value }: FilterOption) => <SeverityItem value={value as string} />;
const SourceNameOption = ({ value }: FilterOption) => (
  <ItemWithLogo variant={IconVariant.sourcesMapByName} type={value as string} textStyle={{ overflow: 'auto' }} />
);

interface FilterByListProps {
  label: ReactNode;
  isLoadingOptions: boolean;
  options: FilterOption[];
  value: unknown[];
  onChange: (field: string, val: unknown) => void;
  field: string;
  type: string;
  onFilterChange: ((filter: Filter) => void) | undefined;
  totalOptionsCount: number;
  emptyState?: ReactNode;
  isSchemaField: boolean;
  setSearch: (search: string) => void;
  open?: boolean;
}

export const FilterByList: React.FC<FilterByListProps> = ({
  label,
  isLoadingOptions,
  options,
  value,
  onChange,
  field,
  type,
  onFilterChange,
  totalOptionsCount,
  emptyState,
  isSchemaField,
  setSearch,
  open = false,
}) => {
  const { excludeNullFromList } = useExpressionBuilder();

  const { isBooleanField, isTextField } = getFieldTypeInfo(type);
  const isSeverity = field.split('.')[1] === 'severity';

  const [displayOptions, setDisplayOptions] = useState(options);
  const [localValue, setLocalValue] = useState(value);
  const optionsRef = useRef(options);
  const localValueRef = useRef(localValue);
  optionsRef.current = options;
  localValueRef.current = localValue;

  const optionsKey = options.map(o => (o.value === null ? 'null' : String(o.value))).join('\u0000');

  useEffect(() => {
    const valueKey = value.map(v => (v === null ? 'null' : String(v))).join('\u0000');
    const localKey = localValueRef.current.map(v => (v === null ? 'null' : String(v))).join('\u0000');
    if (valueKey !== localKey) {
      setLocalValue(value);
    }
  }, [value]);

  useEffect(() => {
    const currentOptions = optionsRef.current;

    if (!open) {
      setDisplayOptions(currentOptions);
      return;
    }

    const currentValue = localValueRef.current || [];
    const selectedSet = new Set(currentValue);
    const optionByValue = new Map(currentOptions.map(o => [o.value, o]));
    const selectedOptions: FilterOption[] = [];
    const seen = new Set();

    currentValue.forEach(selectedValue => {
      if (selectedValue === null && excludeNullFromList) {
        return;
      }
      if (seen.has(selectedValue)) {
        return;
      }
      seen.add(selectedValue);
      selectedOptions.push(
        optionByValue.get(selectedValue) ?? {
          value: selectedValue,
          title: selectedValue === null ? 'Empty' : String(selectedValue),
        }
      );
    });

    const otherOptions = currentOptions.filter(o => !selectedSet.has(o.value));
    const orderedOptions = [...selectedOptions, ...otherOptions];
    const shouldSlice = isSchemaField && orderedOptions.length > MAX_DISPLAYED_OPTIONS && selectedOptions.length <= MAX_DISPLAYED_OPTIONS;
    setDisplayOptions(shouldSlice ? orderedOptions.slice(0, MAX_DISPLAYED_OPTIONS) : orderedOptions);
  }, [open, optionsKey, excludeNullFromList, isSchemaField]);

  const handleChange = useCallback(
    (val: unknown[]) => {
      setLocalValue(val);
      startTransition(() => {
        (onChange as (field: string | string[], val: unknown) => void)([field], val);
        if (type === 'string') {
          const hasNull = val.includes(null);
          const nonNullValues = val.filter(v => v !== null) as string[];
          onFilterChange?.({
            expression: {
              fieldName: field,
              stringCondition:
                val.length > 0 ? { in: { values: nonNullValues }, ...(hasNull && { addNullFilter: true }) } : { contains: '' },
            },
          });
        }
      });
    },
    [field, onChange, onFilterChange, type, setLocalValue]
  );

  // `isExcluded` is only ever set by useFieldFilterOptions, which gates it on the consumer opting in - so with the
  // flag off there is nothing to mark and the base renderer is passed through untouched.
  const hasExcludedOptions = displayOptions.some(o => o.isExcluded);

  const renderBase = isSeverity ? SeverityOption : field.endsWith('.source_names') ? SourceNameOption : undefined;

  const renderOption = useMemo(() => {
    if (!hasExcludedOptions) {
      return renderBase;
    }
    // With no base renderer, `option.title` is what Select's own default (`getLabelFunc`) would have rendered.
    return (option: FilterOption) => {
      const content = renderBase ? renderBase(option) : option.title;
      return option.isExcluded ? <ExcludedOptionLabel>{content}</ExcludedOptionLabel> : content;
    };
  }, [renderBase, hasExcludedOptions]);

  const renderOptionStyle = useCallback(
    (option: OptionObject<unknown>): SxProps => ((option as unknown as FilterOption).isExcluded ? excludedOptionStyle : emptyStyle),
    []
  );

  const filterFunc = useCallback(
    (opts: FilterOption[], { inputValue }: { inputValue: string }) => {
      setSearch(inputValue);
      return opts;
    },
    [setSearch]
  );

  const renderEmptyState = emptyState ? <div className="max-w-96 whitespace-pre-line text-left">{emptyState}</div> : undefined;

  return (
    <Box className="mt-2 block px-2 pb-2">
      <Select
        showOnlyAutoComplete
        size="small"
        acceptNullValue={!excludeNullFromList}
        label={label as React.ReactNode}
        variant="filter"
        loading={isLoadingOptions}
        isMultiple
        enableBulkSelection={!isBooleanField}
        bulkSelectionOptions={options as unknown as OptionObject<unknown>[]}
        showInput={isTextField}
        options={displayOptions as unknown as OptionObject<unknown>[]}
        value={localValue}
        onChange={handleChange}
        totalOptionsCount={totalOptionsCount}
        inputToolbar={renderEmptyState}
        emptyState={emptyState ? null : undefined}
        renderOption={renderOption as unknown as (option: OptionObject<unknown>) => ReactNode}
        renderOptionStyle={renderOptionStyle}
        filterFunc={
          isSchemaField
            ? (filterFunc as unknown as (options: OptionObject<unknown>[], state: { inputValue: string }) => OptionObject<unknown>[])
            : undefined
        }
      />
    </Box>
  );
};
